import {ChangeDetectionStrategy, Component, inject, Input, OnInit, SimpleChanges} from '@angular/core';
import {merge, Observable, of} from 'rxjs';
import {
  AbstractAuthorizedToolbarButtonsComponent,
  AuthorizedButton,
  CaseFileContentAction,
  CaseFileContentToolbarButtonsService,
  CirculationView,
  CounterTypeGroup,
  DocumentAndFileToolbarButtonsService,
  DocumentView,
  ELASTIC_RELOAD_DELAY,
  ExtendedRegistryOfficeTransferActivityDto,
  FileDetailCountType,
  FileDetailService,
  FileToolbarAction,
  FileToolbarButtonsService,
  FileView,
  FolderShareableObjectToolbarAction,
  increment,
  load,
  MainMenuCountsService,
  ObjectDetailPart,
  ShareableObjectToolbarButtonsService,
  ShareableObjectToolbarView,
  StorableObjectToolbarAction,
  StorableObjectToolbarButtonsService,
  StorableObjectView,
  TransferableObjectToolbarAction,
  TransferableObjectToolbarButtonsService,
  TransferableObjectView
} from '|shared';
import {delay} from 'rxjs/operators';
import {takeUntilDestroyed} from '@angular/core/rxjs-interop';
import {FileDto, RegistryOfficeTransferDto} from '|api/document';
import {FileState} from '|api/commons';
import {Router} from '@angular/router';
import {AutoFocusDirective, PopoverComponent, PopoverContentDirective, SpinnerComponent} from '@icz/angular-essentials';
import {CdkOverlayOrigin} from '@angular/cdk/overlay';
import {FormAutocompleteComponent} from '@icz/angular-form-elements';
import {AuthorizedEntityType} from '|api/core';
import {
  ButtonCollectionComponent,
  TOOLBAR_BUTTONS_COMPONENT_STYLES,
  TOOLBAR_BUTTONS_COMPONENT_TEMPLATE
} from '@icz/angular-table';

@Component({
  selector: 'icz-file-toolbar-buttons',
  providers: [
    FileToolbarButtonsService,
    CaseFileContentToolbarButtonsService,
    StorableObjectToolbarButtonsService,
    TransferableObjectToolbarButtonsService,
    DocumentAndFileToolbarButtonsService,
    ShareableObjectToolbarButtonsService
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: TOOLBAR_BUTTONS_COMPONENT_TEMPLATE,
  styles: TOOLBAR_BUTTONS_COMPONENT_STYLES,
  imports: [SpinnerComponent, ButtonCollectionComponent, CdkOverlayOrigin, PopoverComponent, FormAutocompleteComponent, AutoFocusDirective, PopoverContentDirective]
})
export class FileToolbarButtonsComponent extends AbstractAuthorizedToolbarButtonsComponent<FileDto, DocumentView | CirculationView> implements OnInit {

  private fileToolbarButtonsService = inject(FileToolbarButtonsService);
  private shareableObjectToolbarButtonsService = inject(ShareableObjectToolbarButtonsService);
  private caseFileContentToolbarButtonsService = inject(CaseFileContentToolbarButtonsService);
  private storableObjectToolbarButtonsService = inject(StorableObjectToolbarButtonsService);
  private transferableObjectToolbarButtonsService = inject(TransferableObjectToolbarButtonsService);
  private documentAndFileToolbarButtonsService = inject(DocumentAndFileToolbarButtonsService);
  private mainMenuCountsService = inject(MainMenuCountsService);
  private fileDetailService = inject(FileDetailService);
  private router = inject(Router);

  @Input()
  isRegistryOfficeTransferActivityView!: boolean;
  @Input()
  selectedFile: Nullable<FileDto>;

  ngOnInit() {
    merge(
      this.fileToolbarButtonsService.actionCompleted$,
      this.caseFileContentToolbarButtonsService.actionCompleted$,
      this.storableObjectToolbarButtonsService.actionCompleted$,
      this.transferableObjectToolbarButtonsService.actionCompleted$,
      this.shareableObjectToolbarButtonsService.actionCompleted$,
      this.documentAndFileToolbarButtonsService.actionCompleted$,
    ).pipe(
      delay(ELASTIC_RELOAD_DELAY),
      takeUntilDestroyed(this.destroyRef),
    ).subscribe(action => {
      this.router.navigate([this.router.url.split('?')[0]]);
      switch (action) {
        case FileToolbarAction.FILE_SETTLED:
          this.fileDetailService.reloadObject({
            [ObjectDetailPart.OBJECT_DATA]: load(),
            [ObjectDetailPart.OBJECT_PERMISSIONS]: load(),
          });
          break;
        case FileToolbarAction.FILE_CLOSED:
          this.fileDetailService.reloadObject({
            [ObjectDetailPart.OBJECT_DATA]: load(),
            [ObjectDetailPart.OBJECT_PERMISSIONS]: load(),
          });
          break;
        case FileToolbarAction.FILE_SETTLEMENT_WITHDRAWN:
          this.fileDetailService.reloadObject({
            [ObjectDetailPart.OBJECT_DATA]: load(),
            [ObjectDetailPart.OBJECT_PERMISSIONS]: load(),
          });
          break;
        case FileToolbarAction.FILE_REOPENED:
          this.fileDetailService.reloadObject({
            [ObjectDetailPart.OBJECT_DATA]: load(),
            [ObjectDetailPart.OBJECT_PERMISSIONS]: load(),
          });
          break;
        case FileToolbarAction.FILE_DEACTIVATED:
          this.fileDetailService.reloadObject({
            [ObjectDetailPart.OBJECT_DATA]: load(),
            [ObjectDetailPart.OBJECT_PERMISSIONS]: load(),
          });
          break;
        case FileToolbarAction.FILE_HANDED_OVER_TO_EXTERNAL_APP:
          this.fileDetailService.reloadObject({
            [ObjectDetailPart.OBJECT_DATA]: load(),
            [ObjectDetailPart.OBJECT_PERMISSIONS]: load(),
            [FileDetailCountType.ACTIVITIES]: increment(),
          });
          break;
        case FileToolbarAction.FILE_CIRCULATION_STARTED:
          this.fileDetailService.reloadObject({
            [ObjectDetailPart.OBJECT_PERMISSIONS]: load(),
            [FileDetailCountType.ACTIVITIES]: increment(),
          });
          break;
        case FileToolbarAction.FILE_SETTLE_STARTED:
          this.fileDetailService.reloadObject({
            [ObjectDetailPart.OBJECT_DATA]: load(),
          });
          break;
        case FileToolbarAction.FILE_REACTIVATED:
          break;
        case CaseFileContentAction.ADDED_TO_CASE_FILE:
          this.fileDetailService.reloadObject({
            [ObjectDetailPart.OBJECT_DATA]: load(),
            [ObjectDetailPart.OBJECT_PERMISSIONS]: load(),
          });
          break;
        case CaseFileContentAction.REMOVED_FROM_CASE_FILE:
          this.fileDetailService.reloadObject({
            [ObjectDetailPart.OBJECT_DATA]: load(),
            [ObjectDetailPart.OBJECT_PERMISSIONS]: load(),
          });
          break;
        case CaseFileContentAction.MOVED_TO_CASE_FILE:
          this.fileDetailService.reloadObject({
            [ObjectDetailPart.OBJECT_DATA]: load(),
            [ObjectDetailPart.OBJECT_PERMISSIONS]: load(),
          });
          break;
        case StorableObjectToolbarAction.OBJECT_ADDED_TO_STORAGE_UNIT:
          this.fileDetailService.reloadObject({
            [ObjectDetailPart.OBJECT_DATA]: load(),
            [ObjectDetailPart.OBJECT_PERMISSIONS]: load(),
          });
          break;
        case StorableObjectToolbarAction.OBJECT_REMOVED_FROM_STORAGE_UNIT:
          this.fileDetailService.reloadObject({
            [ObjectDetailPart.OBJECT_DATA]: load(),
            [ObjectDetailPart.OBJECT_PERMISSIONS]: load(),
          });
          break;
        case TransferableObjectToolbarAction.TRANSFER_HANDOVER:
          this.fileDetailService.reloadObject({
            [ObjectDetailPart.OBJECT_DATA]: load(),
            [ObjectDetailPart.OBJECT_PERMISSIONS]: load(),
          });
          break;
        case TransferableObjectToolbarAction.TRANSFER_TAKEOVER:
        case TransferableObjectToolbarAction.TRANSFER_REJECT:
          this.fileDetailService.reloadObject({
            [ObjectDetailPart.OBJECT_DATA]: load(),
            [ObjectDetailPart.OBJECT_PERMISSIONS]: load(),
          });
          break;
        case FolderShareableObjectToolbarAction.OBJECT_INSERTED_INTO_FOLDER:
        case FolderShareableObjectToolbarAction.OBJECT_REMOVED_FROM_FOLDER:
          this.fileDetailService.reloadObject({
            [ObjectDetailPart.OBJECT_DATA]: load(),
            [ObjectDetailPart.OBJECT_PERMISSIONS]: load(),
          });
          break;
        case TransferableObjectToolbarAction.TRANSFER_CANCEL:
        case TransferableObjectToolbarAction.TRANSFER_RETRANSFER:
        case TransferableObjectToolbarAction.TRANSFER_RETURN_TO_OFFICE:
          this.fileDetailService.reloadObject({
            [ObjectDetailPart.OBJECT_DATA]: load(),
          });
          this.mainMenuCountsService.updateMainMenuCounters([
            CounterTypeGroup.DOCUMENT_FILE_TASKS_RECEIVED_CONSIGNMENTS_TRANSFERS_COUNTS,
          ]);
          break;
        default:
          this.fileDetailService.reloadObject();
      }
    });
  }

  override ngOnChanges(changes: SimpleChanges<this>): void {
    if (changes.selectedRows || changes.isRegistryOfficeTransferActivityView) {
      this.generateToolbarContents(this.selectedRows());
    }
  }

  protected getButtonObs(rows: Array<FileDto>) {
    if (rows.length) {
      const file = rows[0];

      if (this.isRegistryOfficeTransferActivityView) {
        return this.transferableObjectToolbarButtonsService.getToolbarButtons(
          [this.fileAndTransferToExtendedTransferDto(file, file.registryOfficeTransfer!)],
          TransferableObjectView.ENTITY_TRANSFER_IN_PROGRESS
        );
      }
      else {
        let buttons$: Observable<AuthorizedButton[]> = of([]);

        if (this.fileDetailService.object.fileState === FileState.SETTLED || this.fileDetailService.object.fileState === FileState.CLOSED) {
          buttons$ = this.toolbarDataService.mergeToolbars(
            this.storableObjectToolbarButtonsService.getToolbarButtons(rows as FileDto[], StorableObjectView.FILE_DETAIL),
            this.transferableObjectToolbarButtonsService.getToolbarButtons(rows as FileDto[], TransferableObjectView.FILE_DETAIL),
            this.fileToolbarButtonsService.getToolbarButtons(rows as FileDto[]),
            this.documentAndFileToolbarButtonsService.getToolbarButtons(rows as FileDto[], {viewType: FileView.FILE_DETAIL}),
            this.shareableObjectToolbarButtonsService.getToolbarButtons(rows as FileDto[], {view: ShareableObjectToolbarView.TABLE}),
            this.caseFileContentToolbarButtonsService.getToolbarButtons(rows as FileDto[], {viewType: FileView.FILE_DETAIL}),
          );
        }
        else if (this.fileDetailService.object.fileState === FileState.DEACTIVATED) {
          buttons$ = this.toolbarDataService.mergeToolbars(
            this.fileToolbarButtonsService.getToolbarButtons(rows as FileDto[]),
            this.documentAndFileToolbarButtonsService.getToolbarButtons(rows as FileDto[], {viewType: FileView.FILE_DETAIL}),
          );
        }
        else if (this.fileDetailService.object.fileState === FileState.EXPORTED) {
          if (this.fileDetailService.object.agendaTransferConfirmed) {
            buttons$ = this.documentAndFileToolbarButtonsService.getToolbarButtons(rows as FileDto[], {viewType: FileView.FILE_DETAIL});
          }
        }
        else {
          buttons$ = this.toolbarDataService.mergeToolbars(
            this.fileToolbarButtonsService.getCreateFileContentToolbarButtons(rows as FileDto[]),
            this.fileToolbarButtonsService.getToolbarButtons(rows as FileDto[]),
            this.documentAndFileToolbarButtonsService.getToolbarButtons(rows as FileDto[], {viewType: FileView.FILE_DETAIL}),
            this.shareableObjectToolbarButtonsService.getToolbarButtons(rows as FileDto[], {view: ShareableObjectToolbarView.TABLE}),
            this.caseFileContentToolbarButtonsService.getToolbarButtons(rows as FileDto[], {viewType: FileView.FILE_DETAIL}),
          );
        }

        return this.authorizedButtonService.evaluateAuthorizedButtons(
          buttons$,
          {
            [AuthorizedEntityType.FILE]: [file.id],
          },
          rows.length === 1,
        );
      }
    }
    else {
      return of([]);
    }
  }

  private fileAndTransferToExtendedTransferDto(file: FileDto, transfer: RegistryOfficeTransferDto) {
    return {
      ...file,
      ...transfer
    } as unknown as ExtendedRegistryOfficeTransferActivityDto;
  }

}
